import { useEffect, useState } from "react";
import { withTranslation } from "next-i18next";
import InputButtonsSameSize from "@components/core/InputButtonsSameSize";
import sortSizes from "@utils/sortSizes";
import sizeTypes from "@utils/constants/sizeTypes";
import catalogContexts from "@utils/constants/catalogContexts";
import SizesGuide from "./SizesGuide";
import SizeReferenceLabel from "./SizeReferenceLabel";

const ProductVariants = ({
  closetItemInCloset,
  product,
  selectedVariant,
  setSelectedVariant,
  context,
  t,
}) => {
  const [sizeInseamList, setSizeInseamList] = useState([]);
  const [mainSizeSelected, setMainSizeSelected] = useState(null);

  function initVariantSelected() {
    if (product.variants.length === 1) {
      const variant = product.variants[0];
      const newSelectedVariant = variant.available ? variant : null;

      setSelectedVariant(newSelectedVariant);
      return newSelectedVariant;
    }

    if (closetItemInCloset && context === catalogContexts.swap) {
      const newSelectedVariant = closetItemInCloset.variant;

      setSelectedVariant(newSelectedVariant);
      return newSelectedVariant;
    }

    setSelectedVariant(null);
    return null;
  }

  function customVariant(variant) {
    if (!variant) return null;

    let mainSizeValue;
    let sizeInseamValue;

    if (variant.size && variant.size.value.charAt(2) === "x") {
      [mainSizeValue, sizeInseamValue] = variant.size.value.split('x');
    } else {
      mainSizeValue = variant.size ? variant.size.value : t("product:unique_size");
    }

    return {
      ...variant,
      mainSize: mainSizeValue,
      sizeInseam: sizeInseamValue,
    };
  }

  function translateSize(size) {
    switch(product.size_type) {
      case sizeTypes.size_tu:
      case sizeTypes.size_multiple:
        return t("product:unique_size");
      default:
        return t(`common:size_display_converter.${product.size_type}.${size}`);
    }
  }

  const variantsList = product.variants.map((variant) => customVariant(variant));
  const customVariantSelected = customVariant(selectedVariant);
  const mainSizeFullList = variantsList.map((variant) => variant.mainSize);
  const mainSizeList = [...new Set(mainSizeFullList)];

  function findSizeInseamList(initiatedVariantSelected) {
    const customInitiatedVariantSelected = customVariant(initiatedVariantSelected);
    if (variantsList.some((variant) => variant.sizeInseam)) {
      if (customInitiatedVariantSelected) {
        const filteredSizeInseamList = variantsList
          .filter((el) => el.mainSize === customInitiatedVariantSelected.mainSize)
          .map((v) => v.sizeInseam);

        setSizeInseamList(filteredSizeInseamList);
      } else {
        const sizeInseamFullList = variantsList.map((variant) => variant.sizeInseam);

        setSizeInseamList([...new Set(sizeInseamFullList)]);
      }
    } else {
      setSizeInseamList([]);
    }
  }

  function handleChangeHips(value) {
    const variant = variantsList.find((el) => el.mainSize === value);

    if (variant.sizeInseam) {
      const filteredVariants = variantsList
        .filter((el) => el.mainSize === value).map((v) => v.sizeInseam);

      if (customVariantSelected) {
        setSelectedVariant(null);
      }

      setMainSizeSelected(value);
      setSizeInseamList(filteredVariants);
    } else {
      if (!variant.available) return;
      if (customVariantSelected && customVariantSelected.id === variant.id) return;

      setMainSizeSelected(value);
      setSelectedVariant(variant);
    }
  }

  function isMainSizeSelected(mainSize) {
    if (mainSizeSelected) return mainSizeSelected === mainSize;

    return customVariantSelected && customVariantSelected.mainSize === mainSize;
  }

  function isMainSizeUnavailable(mainSize) {
    return !variantsList.some((el) => el.mainSize === mainSize && el.available === true);
  }

  function handleChangeInseam(sizeInseam) {
    const variant = variantsList.find(
      (el) => el.sizeInseam === sizeInseam && el.mainSize === mainSizeSelected,
    );

    if (customVariantSelected && customVariantSelected.id === variant && variant.id) return;
    setSelectedVariant(variant);
  }

  function isSizeInseamUnavailable(sizeInseam) {
    if (customVariantSelected && customVariantSelected.size?.value === `${mainSizeSelected}x${sizeInseam}`) return null;

    const variant = variantsList.find(
      (el) => el.sizeInseam === sizeInseam && el.mainSize === mainSizeSelected,
    );

    return variant && !variant.available;
  }

  useEffect(() => {
    const initiatedVariantSelected = initVariantSelected();
    findSizeInseamList(initiatedVariantSelected);

    setMainSizeSelected(null);
  }, [product?.slug, closetItemInCloset, context]);

  if (!product || product.variants.length === 0) return null;

  return (
    <div className="mb-4">
      <SizeReferenceLabel
        product={product}
        selectedVariant={mainSizeSelected || customVariantSelected?.mainSize}
      />

      {sizeInseamList.length > 0 && (
        <p>{t("product:size_hips")}</p>
      )}
      <InputButtonsSameSize
        containerClassName="d-flex align-items-center flex-wrap gap-2"
        options={sortSizes(mainSizeList).map((mainSize) => ({
          title: translateSize(mainSize),
          value: mainSize,
          disabled: !isMainSizeSelected(mainSize) && isMainSizeUnavailable(mainSize),
        }))}
        selectedValues={[mainSizeSelected || (customVariantSelected && customVariantSelected.mainSize)]}
        onClick={(newMainSize) => handleChangeHips(newMainSize)}
      />

      {sizeInseamList.length > 0 && (
        <div>
          <p className="my-3">{t("product:size_inseam")}</p>
          <InputButtonsSameSize
            containerClassName="d-flex align-items-center flex-wrap gap-2"
            options={sortSizes(sizeInseamList).map((sizeInseam) => ({
              title: translateSize(sizeInseam),
              value: sizeInseam,
              disabled: isSizeInseamUnavailable(sizeInseam),
            }))}
            selectedValues={[customVariantSelected && customVariantSelected.sizeInseam]}
            onClick={(newSizeInseam) => handleChangeInseam(newSizeInseam)}
          />
        </div>
      )}

      <SizesGuide product={product} />
    </div>
  );
};

export default withTranslation("product")(ProductVariants);
